import { useLocation, useNavigate } from "react-router-dom"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { createRef, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { toJS } from "mobx"
import MainForm, { MainFormRef } from "../../components/Form/MainForm"
import { Col, Row, Button } from "antd"
import { useTranslation } from "react-i18next"
import { Card } from "../../styledComponents/CustomCard/Card"
import { SurveysViewModel } from "./SurveysViewModel"
import { Survey } from "../../../domain/entities/Survey"
import SurveyInformationForm from "./components/SurveyInformationForm"
import SurveySectionsForm from "./components/SurveySectionsForm"
import SurveyResponsesSection from "./components/SurveyResponsesSection"
import SurveyUsersCompletedSection from "./components/SurveyUsersCompletedSection"
import Switch from "../../components/Switch/Switch"
import { DownloadOutlined } from "@ant-design/icons"
import { SurveyOption } from "../../survey/SurveyOption"
import { RoutePaths } from "../../navigation/RoutePaths.enum"

const SurveysFormView = () => {
	const { t } = useTranslation("surveys", { keyPrefix: "form" })
	const navigate = useNavigate()
	const locationState: { survey: Survey; option: SurveyOption } = useLocation().state
	const formRef = createRef<MainFormRef>()
	const viewModel = useContainerInjection<SurveysViewModel>("SurveysViewModel")
	const [formModified, setFormModified] = useState(false)
	const [editMode, setEditMode] = useState(locationState?.survey?.id !== undefined)
	const [duplicateMode, setDuplicateMode] = useState(locationState?.option === SurveyOption.DUPLICATE)

	useEffect(() => {
		const selectedSurvey = toJS(locationState?.survey)

		if (!selectedSurvey) {
			viewModel.setSurveyFormData({
				id: undefined,
				title: "",
				title_en: "",
				title_ca: "",
				description: "",
				description_en: "",
				description_ca: "",
				status: true,
				microsites: [],
				sections: [],
				marketplaces: [],
				image: undefined,
				adminRecommendation: ""
			})
			setEditMode(false)
			setDuplicateMode(false)
			viewModel.setEditMode(false)
			viewModel.setError("")
			return
		}

		const newFormData = {
			...structuredClone({
				...selectedSurvey,
				status: selectedSurvey?.status ?? true
			})
		}
		const formData = { ...newFormData }
		if (locationState?.option !== SurveyOption.DUPLICATE) {
			formData.id = selectedSurvey?.id
		} else {
			formData.id = undefined
			formData.title = `${selectedSurvey.title} (Copy)`
			formData.microsites = []
		}

		viewModel.setSurveyFormData(formData)

		const isDuplicate = locationState?.option === SurveyOption.DUPLICATE
		const isEdit = selectedSurvey?.id !== undefined && !isDuplicate

		setEditMode(isEdit)
		setDuplicateMode(isDuplicate)
		viewModel.setEditMode(isEdit)
		viewModel.setError("")

		if (isEdit && viewModel.surveys.length === 0) {
			viewModel.fetchSurveys()
		}
	}, [locationState?.survey, locationState?.option])

	const onSubmitForm = async () => {
		if (editMode && !duplicateMode) {
			await viewModel.updateSurvey()
		} else {
			const surveyId = await viewModel.createSurvey()
			if (surveyId) {
				// Redirect to the edition URL with ID
				navigate(RoutePaths.SURVEYS_EDIT.replace(":id", surveyId.toString()), {
					replace: true,
					state: {
						survey: {
							...toJS(viewModel.surveyFormData),
							id: surveyId
						},
						option: SurveyOption.EDIT
					}
				})
				setEditMode(true)
				viewModel.setEditMode(true)
			}
		}
		setFormModified(false)
		// Scroll to the top of form after saved
		window.scrollTo({ top: 0, behavior: "smooth" })
	}

	return (
		<div>
			<MainForm
				onSubmitForm={onSubmitForm}
				initialValues={viewModel.surveyFormData}
				formModified={formModified}
				ref={formRef}
			>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<Card
							bordered={false}
							className="criclebox tablespace mb-24"
							title={
								<div className="flex justify-between items-center">
									<h6 className="font-semibold m-0">{t("surveyInfo.cardTitle")}</h6>
									<div className="flex items-center gap-4">
										<Button
											icon={<DownloadOutlined />}
											type="link"
											disabled={
												viewModel.isLoading || !locationState?.survey?.id || duplicateMode
											}
											onClick={() => {
												if (locationState?.survey?.id) {
													viewModel.exportSurveys(
														locationState.survey.id,
														locationState.survey.title
													)
												}
											}}
										>
											{t("export")}
										</Button>

										<Button
											icon={<DownloadOutlined />}
											type="link"
											disabled={
												viewModel.isLoading || !locationState?.survey?.id || duplicateMode
											}
											onClick={() => {
												if (locationState?.survey?.id) {
													viewModel.exportSurveyOptions(
														locationState.survey.id,
														locationState.survey.title
													)
												}
											}}
										>
											{t("exportOptions")}
										</Button>

										<Switch
											title={t("surveyInfo.status")}
											checked={viewModel.surveyFormData.status}
											loading={viewModel.isLoading}
											containerStyles={{ marginBottom: 0 }}
											titleStyles={{ fontSize: "1rem" }}
											onChange={value => {
												viewModel.setSurveyFormData({
													...viewModel.surveyFormData,
													status: value
												})
											}}
										/>
									</div>
								</div>
							}
							bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
						>
							<SurveyInformationForm
								formData={viewModel.surveyFormData}
								onChange={newFormData => {
									viewModel.setSurveyFormData({ ...viewModel.surveyFormData, ...newFormData })
									setFormModified(true)
								}}
							/>
						</Card>
						<Card
							bordered={false}
							className="criclebox tablespace mb-24"
							title={<h6 className="font-semibold m-0">{t("sections.cardTitle")}</h6>}
							bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
						>
							<SurveySectionsForm
								formData={viewModel.surveyFormData}
								onChange={newFormData => {
									viewModel.setSurveyFormData({ ...viewModel.surveyFormData, ...newFormData })
									setFormModified(true)
								}}
								answers={locationState?.survey?.answers}
							/>
						</Card>
						{editMode && locationState?.survey?.id && (
							<>
								<Card
									bordered={false}
									className="criclebox tablespace mb-24"
									title={<h6 className="font-semibold m-0">{t("usersCompleted.cardTitle")}</h6>}
									bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
								>
									<SurveyUsersCompletedSection
										survey={(() => {
											const fullSurvey = viewModel.surveys.find(
												s => s.id === locationState.survey?.id
											)
											const survey = fullSurvey || locationState.survey
											return survey
										})()}
										viewModel={viewModel}
									/>
								</Card>

								<Card
									bordered={false}
									className="criclebox tablespace mb-24"
									title={<h6 className="font-semibold m-0">{t("adminRecommendation.cardTitle")}</h6>}
									bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
								>
									<SurveyInformationForm
										formData={viewModel.surveyFormData}
										onChange={newFormData => {
											viewModel.setSurveyFormData({ ...viewModel.surveyFormData, ...newFormData })
											setFormModified(true)
										}}
										showAdminRecommendation={true}
									/>
								</Card>

								<Card
									bordered={false}
									className="criclebox tablespace mb-24"
									title={<h6 className="font-semibold m-0">{t("responses.cardTitle")}</h6>}
									bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
								>
									<SurveyResponsesSection
										answers={locationState.survey.answers || []}
										sections={locationState.survey.sections || []}
										viewModel={viewModel}
									/>
								</Card>
							</>
						)}
					</Col>
				</Row>
			</MainForm>
		</div>
	)
}

export default observer(SurveysFormView)
